import React, { useState, useEffect } from 'react';
import {
  getAdminReferralStats,
  getLeaderboard,
  getUserReferralData,
  adjustUserBalance,
  updateReferralStatus,
  getMilestones,
  saveMilestone,
  deleteMilestone,
  exportReferralData,
} from '../../api/referrals';
import './AdminReferrals.css';

const AdminReferrals = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [filters, setFilters] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [editingMilestone, setEditingMilestone] = useState(null);

  useEffect(() => {
    loadData();
  }, [activeTab, filters]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'overview') {
        const data = await getAdminReferralStats(filters);
        setStats(data);
      } else if (activeTab === 'leaderboard') {
        const data = await getLeaderboard(50);
        setLeaderboard(data);
      } else if (activeTab === 'milestones') {
        const data = await getMilestones();
        setMilestones(data);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      await exportReferralData(filters);
    } catch (error) {
      console.error('Error exporting data:', error);
      alert('Failed to export data');
    }
  };

  const handleViewUser = async (userId) => {
    try {
      const data = await getUserReferralData(userId);
      setSelectedUser(data);
    } catch (error) {
      console.error('Error loading user data:', error);
    }
  };

  const handleAdjustBalance = async (userId) => {
    const credit = prompt('Enter credit amount (can be negative):');
    const cash = prompt('Enter cash amount (can be negative):');
    const reason = prompt('Reason for adjustment:');

    if (reason) {
      try {
        await adjustUserBalance(userId, parseFloat(credit) || 0, parseFloat(cash) || 0, reason);
        alert('Balance adjusted successfully');
        loadData();
        if (selectedUser) {
          handleViewUser(userId);
        }
      } catch (error) {
        console.error('Error adjusting balance:', error);
        alert('Failed to adjust balance');
      }
    }
  };

  const handleUpdateReferralStatus = async (referralId, newStatus) => {
    const notes = prompt('Add notes (optional):');
    try {
      await updateReferralStatus(referralId, newStatus, notes);
      alert('Status updated successfully');
      loadData();
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status');
    }
  };

  const handleSaveMilestone = async (milestone) => {
    try {
      await saveMilestone(milestone._id, milestone);
      alert('Milestone saved successfully');
      setEditingMilestone(null);
      loadData();
    } catch (error) {
      console.error('Error saving milestone:', error);
      alert('Failed to save milestone');
    }
  };

  const handleDeleteMilestone = async (id) => {
    if (window.confirm('Are you sure you want to delete this milestone?')) {
      try {
        await deleteMilestone(id);
        alert('Milestone deleted successfully');
        loadData();
      } catch (error) {
        console.error('Error deleting milestone:', error);
        alert('Failed to delete milestone');
      }
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      registered: '#ffa500',
      campaign_started: '#3498db',
      reward_pending: '#9b59b6',
      reward_paid: '#27ae60',
    };
    return colors[status] || '#95a5a6';
  };

  const renderOverview = () => {
    if (!stats) return null;

    return (
      <div className="admin-overview">
        <div className="stats-summary">
          <div className="summary-card">
            <h3>Total Referrals</h3>
            <div className="value">{stats.total_referrals}</div>
          </div>
          <div className="summary-card success">
            <h3>Successful</h3>
            <div className="value">{stats.successful_referrals}</div>
          </div>
          <div className="summary-card credit">
            <h3>Total Credit Issued</h3>
            <div className="value">${stats.total_credit_issued}</div>
          </div>
          <div className="summary-card conversion">
            <h3>Conversion Rate</h3>
            <div className="value">{stats.conversion_rate}%</div>
          </div>
        </div>

        <div className="referrals-table-section">
          <div className="section-header-with-action">
            <h3>Recent Referrals</h3>
            <button onClick={handleExport} className="export-btn">
              Export CSV
            </button>
          </div>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Referrer</th>
                <th>Code</th>
                <th>Referred User</th>
                <th>Date</th>
                <th>Campaign $</th>
                <th>Reward $</th>
              </tr>
            </thead>
            <tbody>
              {stats.referrals.map((referral) => (
                <tr key={referral.id}>
                  <td>
                    <button
                      className="link-btn"
                      onClick={() => handleViewUser(referral.referrer_id)}
                    >
                      {referral.referrer_email}
                    </button>
                  </td>
                  <td>{referral.referrer_code}</td>
                  <td>{referral.referred_email}</td>
                  <td>{new Date(referral.created_at).toLocaleDateString()}</td>
                  <td>{referral.campaign_amount ? `$${referral.campaign_amount}` : '-'}</td>
                  <td>{referral.reward_amount ? `$${referral.reward_amount}` : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="top-referrers-section">
          <h3>Top Referrers</h3>
          <div className="top-referrers-grid">
            {stats.top_referrers.slice(0, 10).map((user, index) => (
              <div key={user.id} className="top-referrer-card">
                <div className="rank">#{index + 1}</div>
                <div className="referrer-info">
                  <strong>{user.email}</strong>
                  <span className="code">{user.referral_code}</span>
                </div>
                <div className="referrer-stats">
                  <div>
                    <span className="label">Total:</span> {user.total_referrals}
                  </div>
                  <div>
                    <span className="label">Success:</span> {user.successful_referrals}
                  </div>
                  <div>
                    <span className="label">Credit:</span> ${user.credit_balance}
                  </div>
                </div>
                <button
                  className="manage-btn"
                  onClick={() => handleViewUser(user.id)}
                >
                  Manage
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderLeaderboard = () => {
    return (
      <div className="leaderboard-section">
        <h2>Referral Leaderboard</h2>
        <table className="admin-table leaderboard-table">
          <thead>
            <tr>
              <th>Rank</th>
              <th>Email</th>
              <th>Code</th>
              <th>Successful</th>
              <th>Credit Balance</th>
              <th>Member Since</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {leaderboard.map((user) => (
              <tr key={user.id} className={user.rank <= 3 ? `rank-${user.rank}` : ''}>
                <td className="rank-cell">
                  {user.rank <= 3 && <span className="medal">🏆</span>}
                  {user.rank}
                </td>
                <td>{user.email}</td>
                <td>{user.referral_code}</td>
                <td>{user.successful_referrals}</td>
                <td>${user.credit_balance}</td>
                <td>{new Date(user.member_since).toLocaleDateString()}</td>
                <td>
                  <button
                    className="manage-btn"
                    onClick={() => handleViewUser(user.id)}
                  >
                    Manage
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderMilestones = () => {
    return (
      <div className="milestones-section">
        <div className="section-header">
          <h2>Milestone Management</h2>
          <button
            className="add-btn"
            onClick={() =>
              setEditingMilestone({
                threshold: 0,
                reward_type: 'physical',
                reward_name: '',
                reward_description: '',
                reward_value: 0,
                is_active: true,
                order: 0,
              })
            }
          >
            Add Milestone
          </button>
        </div>

        {editingMilestone && (
          <div className="milestone-editor">
            <h3>{editingMilestone._id ? 'Edit Milestone' : 'New Milestone'}</h3>
            <div className="form-grid">
              <div className="form-field">
                <label>Threshold (# of referrals)</label>
                <input
                  type="number"
                  value={editingMilestone.threshold}
                  onChange={(e) =>
                    setEditingMilestone({ ...editingMilestone, threshold: parseInt(e.target.value) })
                  }
                />
              </div>
              <div className="form-field">
                <label>Reward Type</label>
                <select
                  value={editingMilestone.reward_type}
                  onChange={(e) =>
                    setEditingMilestone({ ...editingMilestone, reward_type: e.target.value })
                  }
                >
                  <option value="physical">Physical</option>
                  <option value="credit">Credit</option>
                  <option value="cash">Cash</option>
                  <option value="custom">Custom</option>
                </select>
              </div>
              <div className="form-field">
                <label>Reward Name</label>
                <input
                  type="text"
                  value={editingMilestone.reward_name}
                  onChange={(e) =>
                    setEditingMilestone({ ...editingMilestone, reward_name: e.target.value })
                  }
                />
              </div>
              <div className="form-field">
                <label>Reward Value ($)</label>
                <input
                  type="number"
                  value={editingMilestone.reward_value || 0}
                  onChange={(e) =>
                    setEditingMilestone({ ...editingMilestone, reward_value: parseFloat(e.target.value) })
                  }
                />
              </div>
              <div className="form-field full-width">
                <label>Description</label>
                <textarea
                  value={editingMilestone.reward_description}
                  onChange={(e) =>
                    setEditingMilestone({ ...editingMilestone, reward_description: e.target.value })
                  }
                />
              </div>
              <div className="form-field">
                <label>
                  <input
                    type="checkbox"
                    checked={editingMilestone.is_active}
                    onChange={(e) =>
                      setEditingMilestone({ ...editingMilestone, is_active: e.target.checked })
                    }
                  />
                  Active
                </label>
              </div>
            </div>
            <div className="button-group">
              <button className="save-btn" onClick={() => handleSaveMilestone(editingMilestone)}>
                Save
              </button>
              <button className="cancel-btn" onClick={() => setEditingMilestone(null)}>
                Cancel
              </button>
            </div>
          </div>
        )}

        <div className="milestones-list">
          {milestones.map((milestone) => (
            <div key={milestone._id} className="milestone-card">
              <div className="milestone-header">
                <h3>{milestone.reward_name}</h3>
                <span className={`status ${milestone.is_active ? 'active' : 'inactive'}`}>
                  {milestone.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className="milestone-body">
                <p>
                  <strong>Threshold:</strong> {milestone.threshold} referrals
                </p>
                <p>
                  <strong>Type:</strong> {milestone.reward_type}
                </p>
                {milestone.reward_value > 0 && (
                  <p>
                    <strong>Value:</strong> ${milestone.reward_value}
                  </p>
                )}
                {milestone.reward_description && <p>{milestone.reward_description}</p>}
              </div>
              <div className="milestone-actions">
                <button className="edit-btn" onClick={() => setEditingMilestone(milestone)}>
                  Edit
                </button>
                <button className="delete-btn" onClick={() => handleDeleteMilestone(milestone._id)}>
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="admin-referrals">
      <div className="admin-header">
        <h1>Referral System Management</h1>
      </div>

      <div className="tabs">
        <button
          className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
          className={`tab ${activeTab === 'leaderboard' ? 'active' : ''}`}
          onClick={() => setActiveTab('leaderboard')}
        >
          Leaderboard
        </button>
        {/* <button
          className={`tab ${activeTab === 'milestones' ? 'active' : ''}`}
          onClick={() => setActiveTab('milestones')}
        >
          Milestones
        </button> */}
      </div>

      <div className="tab-content">
        {loading ? (
          <div className="loading">
            <div className="spinner"></div>
            <p>Loading...</p>
          </div>
        ) : (
          <>
            {activeTab === 'overview' && renderOverview()}
            {activeTab === 'leaderboard' && renderLeaderboard()}
            {/* {activeTab === 'milestones' && renderMilestones()} */}
          </>
        )}
      </div>

      {/* User Details Modal */}
      {selectedUser && (
        <div className="modal-overlay" onClick={() => setSelectedUser(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>User Referral Details</h2>
            <div className="user-details">
              <p>
                <strong>Email:</strong> {selectedUser.user.email}
              </p>
              <p>
                <strong>Code:</strong> {selectedUser.user.referral_code}
              </p>
              <p>
                <strong>Successful:</strong> {selectedUser.successful_referrals}
              </p>
              <p>
                <strong>Credit Balance:</strong> ${selectedUser.credit_balance}
              </p>
            </div>
            <div className="modal-actions">
              <button
                className="adjust-btn"
                onClick={() => handleAdjustBalance(selectedUser.user.id)}
              >
                Adjust Balance
              </button>
              <button className="close-btn" onClick={() => setSelectedUser(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminReferrals;
